import React, { useState, useEffect } from 'react';
import { FileDown, Calendar, ClipboardList } from 'lucide-react';
import ExcelJS from 'exceljs';
import DateInput from './DateInput';
import './AppointmentOrderForm.css';

// Mock data - matching Form-F.xlsx template placeholders exactly (16 total - Monthly Leave Register)
const mockFormData = {
  // Month period
  mon_st_date: "2025-01-01",
  mon_end_date: "2025-01-31",

  // Total days
  tot_days: "31",

  // Earned Leave (EL)
  leave_st: "28", // Opening balance - Earned Leave
  leave_cred: "2.5", // Credited during month
  leave_end: "30.5", // Closing balance (before taking leave)
  no_leave: "3", // Number of days leave taken
  tol_el: "27.5", // Total balance after leave taken

  // Sick Leave (SL)
  lev_s_st: "12", // Opening balance - Sick Leave
  lev_s_cred: "1", // Credited during month
  lev_s_end: "13", // Closing balance (before taking leave)
  no_s_leave: "2", // Number of sick leave days taken
  bal_s_leave: "11", // Total balance after sick leave taken

  // Leave encashment
  leave_sick: "0", // Sick leave encashed
  leave_amt: "0", // Amount paid for leave encashment
  leave_paydate: "" // Payment date for encashment
};

const Form5EarnedLeave = () => {
  const [formData, setFormData] = useState(mockFormData);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    // Fetch data from API on component mount
    const fetchFormData = async () => {
      try {
        console.log('Fetching Form F (Leave Register) data from API...');
        // const response = await fetch('/api/form-f-data');
        // const data = await response.json();
        // setFormData(data);
        console.log('Using mock data for Form F');
      } catch (error) {
        console.error('Error fetching Form F data:', error);
        console.log('Falling back to mock data');
      }
    };

    fetchFormData();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const generateExcel = async () => {
    setIsGenerating(true);

    try {
      console.log('Starting Form F (Leave Register) Excel generation...');
      console.log('Form Data:', formData);

      // Load the template
      const response = await fetch('/templates/Form-F.xlsx');
      const arrayBuffer = await response.arrayBuffer();

      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);
      const worksheet = workbook.worksheets[0];

      console.log('Template loaded successfully');

      // Remove ALL background fills from the entire worksheet
      worksheet.eachRow({ includeEmpty: true }, (row) => {
        row.eachCell({ includeEmpty: true }, (cell) => {
          if (cell.fill) {
            cell.fill = null;
          }
        });
      });

      console.log('Background fills removed');

      // Single-pass placeholder replacement (all fields at form level)
      console.log('Replacing all placeholders...');
      worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          const cellValue = cell.value ? String(cell.value) : '';

          if (cellValue.includes('{{')) {
            const placeholderRegex = /\{\{\s*([^}]+?)\s*\}\}/g;
            let newValue = cellValue;
            let match;

            while ((match = placeholderRegex.exec(cellValue)) !== null) {
              const fieldName = match[1].trim();
              const normalizedFieldName = fieldName.replace(/\s+/g, '_');

              // Check all fields
              if (formData[normalizedFieldName] !== undefined) {
                console.log(`Row ${rowNumber}, Col ${colNumber}: Replacing {{ ${fieldName} }} with "${formData[normalizedFieldName]}"`);
                newValue = newValue.replace(match[0], formData[normalizedFieldName]);
              }
            }

            if (newValue !== cellValue) {
              cell.value = newValue;
            }
          }
        });
      });

      console.log('All placeholders replaced');

      // Generate Form file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const monthName = new Date(formData.mon_st_date).toLocaleString('default', { month: 'long', year: 'numeric' });
      link.download = `Form-F-Leave-Register-${monthName.replace(' ', '-')}-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('Excel file generated and downloaded successfully');

      setTimeout(() => {
        setIsGenerating(false);
        alert('Form F - Leave Register Excel generated successfully!');
      }, 500);

    } catch (error) {
      console.error('Error generating Excel:', error);
      alert(`Error generating Excel: ${error.message}`);
      setIsGenerating(false);
    }
  };

  return (
    <div className="appointment-order-container">
      <div className="appointment-header">
        <div>
          <h2 className="appointment-title">Form F - Leave Register</h2>
          <p className="appointment-subtitle">Monthly Earned Leave and Sick Leave Register</p>
        </div>
      </div>

      <div className="appointment-content">
        {/* Info Alert */}
        <div className="info-alert">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
          <span>Monthly leave register tracking earned leave and sick leave balances. Review and update before generating Excel.</span>
        </div>

        {/* Month Period */}
        <div className="form-section">
          <div className="section-header">
            <Calendar size={20} />
            <h3>Month Period</h3>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Month Start Date</label>
              <DateInput
                value={formData.mon_st_date}
                onChange={(e) => handleInputChange('mon_st_date', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Month End Date</label>
              <DateInput
                value={formData.mon_end_date}
                onChange={(e) => handleInputChange('mon_end_date', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Total Days in Month</label>
              <input
                type="number"
                value={formData.tot_days}
                onChange={(e) => handleInputChange('tot_days', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Earned Leave Section */}
        <div className="form-section" style={{ background: '#dbeafe', border: '1px solid #93c5fd' }}>
          <div className="section-header">
            <ClipboardList size={20} />
            <h3>Earned Leave (EL) Details</h3>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Opening Balance (Days)</label>
              <input
                type="number"
                step="0.5"
                value={formData.leave_st}
                onChange={(e) => handleInputChange('leave_st', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Leave Credited This Month (Days)</label>
              <input
                type="number"
                step="0.5"
                value={formData.leave_cred}
                onChange={(e) => handleInputChange('leave_cred', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Total Available Before Taking Leave (Days)</label>
              <input
                type="number"
                step="0.5"
                value={formData.leave_end}
                onChange={(e) => handleInputChange('leave_end', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Number of Days Leave Taken</label>
              <input
                type="number"
                step="0.5"
                value={formData.no_leave}
                onChange={(e) => handleInputChange('no_leave', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Total EL Balance (Closing)</label>
              <input
                type="number"
                step="0.5"
                value={formData.tol_el}
                onChange={(e) => handleInputChange('tol_el', e.target.value)}
                style={{ fontWeight: 'bold' }}
              />
            </div>
          </div>
        </div>

        {/* Sick Leave Section */}
        <div className="form-section" style={{ background: '#fef3c7', border: '1px solid #fbbf24' }}>
          <div className="section-header">
            <ClipboardList size={20} />
            <h3>Sick Leave (SL) Details</h3>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Opening Balance (Days)</label>
              <input
                type="number"
                step="0.5"
                value={formData.lev_s_st}
                onChange={(e) => handleInputChange('lev_s_st', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Sick Leave Credited This Month (Days)</label>
              <input
                type="number"
                step="0.5"
                value={formData.lev_s_cred}
                onChange={(e) => handleInputChange('lev_s_cred', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Total Available Before Taking Leave (Days)</label>
              <input
                type="number"
                step="0.5"
                value={formData.lev_s_end}
                onChange={(e) => handleInputChange('lev_s_end', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Number of Sick Leave Days Taken</label>
              <input
                type="number"
                step="0.5"
                value={formData.no_s_leave}
                onChange={(e) => handleInputChange('no_s_leave', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Total SL Balance (Closing)</label>
              <input
                type="number"
                step="0.5"
                value={formData.bal_s_leave}
                onChange={(e) => handleInputChange('bal_s_leave', e.target.value)}
                style={{ fontWeight: 'bold' }}
              />
            </div>
          </div>
        </div>

        {/* Leave Encashment Section */}
        <div className="form-section" style={{ background: '#d1fae5', border: '1px solid #6ee7b7' }}>
          <div className="section-header">
            <ClipboardList size={20} />
            <h3>Leave Encashment (If Applicable)</h3>
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Sick Leave Encashed (Days)</label>
              <input
                type="number"
                step="0.5"
                value={formData.leave_sick}
                onChange={(e) => handleInputChange('leave_sick', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Encashment Amount (₹)</label>
              <input
                type="number"
                value={formData.leave_amt}
                onChange={(e) => handleInputChange('leave_amt', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Payment Date</label>
              <DateInput
                value={formData.leave_paydate}
                onChange={(e) => handleInputChange('leave_paydate', e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Generate Form Button */}
        <div className="form-actions">
          <button
            onClick={generateExcel}
            disabled={isGenerating}
            className={`btn-generate ${isGenerating ? 'generating' : ''}`}
          >
            {isGenerating ? (
              <>
                <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" opacity="0.25"/>
                  <path d="M12 2a10 10 0 0 1 0 20" opacity="0.75"/>
                </svg>
                <span>Generating Excel...</span>
              </>
            ) : (
              <>
                <FileDown size={20} />
                <span>Generate & Download Form F Leave Register Excel</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Form5EarnedLeave;
